import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Carrusel de los últimos contenidos publicados en Viubux.
 * Excluye contenido subido desde YouTube (filtrado en backend).
 * theme: 'violet' | 'cyan'
 */
export const LatestContentCarousel = ({
  title = 'Últimos contenidos publicados',
  subtitle = 'Explora lo más reciente del catálogo Viubux',
  theme = 'violet',
  limit = 7,
  compact = false,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [broken, setBroken] = useState(() => new Set());
  const scrollRef = useRef(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/public/latest-content?limit=${limit + 5}`)
      .then((res) => {
        if (active) setItems(res.data?.items || []);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [limit]);

  const scroll = (dir) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  // Excluir publicaciones sin portada o con portada rota (404)
  const visible = items
    .filter((it) => it.cover_url && !broken.has(it.cover_url))
    .slice(0, limit);

  if (!loading && visible.length === 0) return null;

  const accent = theme === 'cyan' ? 'text-cyan-400' : 'text-violet-400';
  const accentBorder = theme === 'cyan' ? 'border-cyan-500/30' : 'border-violet-500/30';
  const btnBg = theme === 'cyan'
    ? 'bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/30'
    : 'bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/30';

  return (
    <section className={compact ? 'py-6' : 'py-12 md:py-16'} data-testid="latest-content-carousel">
      <div className={compact ? 'px-5' : 'container mx-auto px-4'}>
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="gotham-bold text-2xl md:text-3xl text-white mb-1 flex items-center gap-2">
              <BookOpen className={`w-6 h-6 ${accent}`} />
              {title}
            </h2>
            <p className="text-white/50 text-sm md:text-base">{subtitle}</p>
          </div>
          <div className="hidden md:flex gap-2 shrink-0">
            <button
              onClick={() => scroll('left')}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${btnBg}`}
              data-testid="carousel-prev-btn"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => scroll('right')}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all ${btnBg}`}
              data-testid="carousel-next-btn"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {loading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="shrink-0 w-40 md:w-44 snap-start">
                  <div className="aspect-[2/3] rounded-xl bg-white/5 animate-pulse" />
                  <div className="h-4 mt-2 bg-white/5 rounded animate-pulse" />
                </div>
              ))
            : visible.map((item, i) => (
                <a
                  key={item.cover_url || i}
                  href={item.reader_url || 'https://app.viubux.com'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group shrink-0 w-40 md:w-44 snap-start"
                  data-testid={`content-card-${i}`}
                >
                  <div className={`aspect-[2/3] rounded-xl overflow-hidden border ${accentBorder} bg-slate-800 shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-violet-500/20`}>
                    <img
                      src={item.cover_url}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={() => setBroken((prev) => new Set(prev).add(item.cover_url))}
                    />
                  </div>
                  <h3 className="text-white text-sm font-semibold mt-2 line-clamp-2 leading-tight">
                    {item.title}
                  </h3>
                  {item.author ? (
                    <p className={`text-xs mt-0.5 ${accent} line-clamp-1`}>{item.author}</p>
                  ) : null}
                </a>
              ))}
        </div>
      </div>
    </section>
  );
};

export default LatestContentCarousel;
